import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { SlideFrame } from './SlideFrame';
import { SLIDE_DECK_CANVAS } from './slideDeckCanvas';

type SlideChangeReason = 'init' | 'keyboard' | 'click' | 'api';

interface SlideChangeDetail {
  index: number;
  previousIndex: number;
  total: number;
  reason: SlideChangeReason;
}

interface SlideDeckHarnessProps {
  children: ReactNode;
  isEditor?: boolean;
  className?: string;
  onSlideChange?: (detail: SlideChangeDetail) => void;
}

const OVERLAY_IDLE_MS = 1800;

function clampIndex(value: number, total: number): number {
  if (total <= 0) return 0;
  if (value < 0) return 0;
  if (value >= total) return total - 1;
  return value;
}

function readInitialSlideIndex(total: number): number {
  if (typeof window === 'undefined' || total <= 0) return 0;
  const urlIndex = Number.parseInt(new URLSearchParams(window.location.search).get('slide') ?? '', 10);
  if (!Number.isFinite(urlIndex)) return 0;
  return clampIndex(urlIndex - 1, total);
}

function syncUrlSlide(index: number) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('slide', String(index + 1));
  window.history.replaceState(window.history.state, '', url);
}

export const SlideDeckHarness = React.memo(function SlideDeckHarness({
  children,
  isEditor = false,
  className,
  onSlideChange,
}: SlideDeckHarnessProps) {
  const slides = useMemo(() => React.Children.toArray(children).filter(Boolean), [children]);
  const total = slides.length;

  if (isEditor) {
    return <SlideDeckEditorView slides={slides} total={total} className={className} />;
  }

  return <SlideDeckHarnessRuntime slides={slides} total={total} className={className} onSlideChange={onSlideChange} />;
});

interface EditorViewProps {
  slides: ReactNode[];
  total: number;
  className: string | undefined;
}

function SlideDeckEditorView({ slides, total, className }: EditorViewProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex((current) => clampIndex(current, total));
  }, [total]);

  const goTo = useCallback(
    (compute: (current: number) => number) => {
      setActiveIndex((current) => {
        const next = clampIndex(compute(current), total);
        return next === current ? current : next;
      });
    },
    [total],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          target.getAttribute?.('contenteditable') === 'true' ||
          /^(input|textarea|select)$/i.test(target.tagName))
      ) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      switch (event.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          goTo((c) => c + 1);
          break;
        case 'ArrowLeft':
        case 'PageUp':
          event.preventDefault();
          goTo((c) => c - 1);
          break;
        case 'Home':
          event.preventDefault();
          goTo(() => 0);
          break;
        case 'End':
          event.preventDefault();
          goTo(() => total - 1);
          break;
        default: {
          if (event.key >= '1' && event.key <= '9') {
            event.preventDefault();
            goTo(() => Number.parseInt(event.key, 10) - 1);
          } else if (event.key === '0') {
            event.preventDefault();
            goTo(() => 9);
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goTo, total]);

  if (total === 0) {
    return <div className={cn('text-sm text-gray-500', className)}>Empty deck</div>;
  }

  return (
    <div data-slide-deck-editor className={cn('flex h-screen flex-col gap-3 pb-3', className)}>
      {/* All slides stay mounted so data-slide-active toggles drive Tailwind transitions on
          navigation. Inactive slides park at left:-99999px instead of stacking on top of
          the active slide — keeps dnd-kit collision detection from finding duplicate drop
          zones at the visible slide's coords. */}
      <div className="relative flex w-full min-h-0 flex-1 items-center justify-center overflow-hidden bg-black px-6 py-6">
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={index}
              data-slide-editor-index={index}
              data-slide-active={isActive ? 'true' : 'false'}
              aria-hidden={!isActive}
              className="group/slide w-full"
              style={{
                position: isActive ? 'relative' : 'absolute',
                left: isActive ? undefined : -99999,
                top: isActive ? undefined : 0,
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              <SlideFrame index={index}>{slide}</SlideFrame>
            </div>
          );
        })}
      </div>
      {total > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm text-gray-600">
          <button
            type="button"
            onClick={() => goTo((c) => c - 1)}
            disabled={activeIndex === 0}
            aria-label="Previous slide"
            className="rounded-full border border-gray-200 bg-white px-3 py-1 transition hover:bg-gray-50 disabled:opacity-40"
          >
            ←
          </button>
          <span className="tabular-nums">
            {activeIndex + 1} / {total}
          </span>
          <button
            type="button"
            onClick={() => goTo((c) => c + 1)}
            disabled={activeIndex === total - 1}
            aria-label="Next slide"
            className="rounded-full border border-gray-200 bg-white px-3 py-1 transition hover:bg-gray-50 disabled:opacity-40"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

interface RuntimeProps {
  slides: ReactNode[];
  total: number;
  className: string | undefined;
  onSlideChange: ((detail: SlideChangeDetail) => void) | undefined;
}

function SlideDeckHarnessRuntime({ slides, total, className, onSlideChange }: RuntimeProps) {
  const [activeIndex, setActiveIndex] = useState(() => readInitialSlideIndex(total));
  const [scale, setScale] = useState(1);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const overlayHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const previousIndexRef = useRef(-1);

  // Reclamp when total changes — if slides are removed and activeIndex now exceeds the
  // new bounds, the render loop matches no slide and the viewer goes blank.
  useEffect(() => {
    setActiveIndex((current) => clampIndex(current, total));
  }, [total]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const pendingReasonRef = useRef<SlideChangeReason>('init');
  const onSlideChangeRef = useRef(onSlideChange);
  useEffect(() => {
    onSlideChangeRef.current = onSlideChange;
  }, [onSlideChange]);

  const updateActiveIndex = useCallback(
    (compute: (current: number) => number, reason: SlideChangeReason) => {
      setActiveIndex((current) => {
        const clamped = clampIndex(compute(current), total);
        if (clamped === current) return current;
        previousIndexRef.current = current;
        return clamped;
      });
      pendingReasonRef.current = reason;
    },
    [total],
  );

  const goToSlide = useCallback(
    (index: number, reason: SlideChangeReason) => updateActiveIndex(() => index, reason),
    [updateActiveIndex],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || total === 0) return;
    syncUrlSlide(activeIndex);
    const reason = pendingReasonRef.current;
    const detail: SlideChangeDetail = {
      index: activeIndex,
      previousIndex: previousIndexRef.current,
      total,
      reason,
    };
    onSlideChangeRef.current?.(detail);
    if (stageRef.current) {
      stageRef.current.dispatchEvent(new CustomEvent('slidechange', { detail, bubbles: true, composed: true }));
    }
    pendingReasonRef.current = 'api';
  }, [activeIndex, total]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const computeScale = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const next = Math.min(vw / SLIDE_DECK_CANVAS.width, vh / SLIDE_DECK_CANVAS.height);
      setScale(Number.isFinite(next) && next > 0 ? next : 1);
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, []);

  const next = useCallback(
    (reason: SlideChangeReason = 'keyboard') => updateActiveIndex((current) => current + 1, reason),
    [updateActiveIndex],
  );
  const prev = useCallback(
    (reason: SlideChangeReason = 'keyboard') => updateActiveIndex((current) => current - 1, reason),
    [updateActiveIndex],
  );

  const toggleFullscreen = useCallback(() => {
    if (typeof document === 'undefined') return;
    const root = stageRef.current?.closest('[data-slide-deck-root]') as HTMLElement | null;
    if (!root) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void root.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))) {
        return;
      }
      switch (event.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          next();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          event.preventDefault();
          prev();
          break;
        case 'Home':
          event.preventDefault();
          goToSlide(0, 'keyboard');
          break;
        case 'End':
          event.preventDefault();
          goToSlide(total - 1, 'keyboard');
          break;
        case 'r':
        case 'R':
          if (event.metaKey || event.ctrlKey) return;
          event.preventDefault();
          goToSlide(0, 'keyboard');
          break;
        case 'f':
        case 'F': {
          if (event.metaKey || event.ctrlKey) return;
          event.preventDefault();
          toggleFullscreen();
          break;
        }
        default: {
          if (event.metaKey || event.ctrlKey || event.altKey) return;
          if (event.key >= '1' && event.key <= '9') {
            event.preventDefault();
            goToSlide(Number.parseInt(event.key, 10) - 1, 'keyboard');
          } else if (event.key === '0') {
            event.preventDefault();
            goToSlide(9, 'keyboard');
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, goToSlide, toggleFullscreen, total]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const showAndHide = () => {
      setOverlayVisible(true);
      if (overlayHideTimeoutRef.current) clearTimeout(overlayHideTimeoutRef.current);
      overlayHideTimeoutRef.current = setTimeout(() => setOverlayVisible(false), OVERLAY_IDLE_MS);
    };
    showAndHide();
    window.addEventListener('mousemove', showAndHide);
    window.addEventListener('keydown', showAndHide);
    window.addEventListener('touchstart', showAndHide);
    return () => {
      window.removeEventListener('mousemove', showAndHide);
      window.removeEventListener('keydown', showAndHide);
      window.removeEventListener('touchstart', showAndHide);
      if (overlayHideTimeoutRef.current) clearTimeout(overlayHideTimeoutRef.current);
    };
  }, [activeIndex]);

  if (total === 0) {
    return (
      <div
        data-slide-deck-root
        className={cn('flex h-screen w-screen items-center justify-center bg-black text-white', className)}
      >
        Empty deck
      </div>
    );
  }

  return (
    <div
      data-slide-deck-root
      onDoubleClick={toggleFullscreen}
      className={cn(
        'fixed inset-0 flex h-screen w-screen items-center justify-center overflow-hidden bg-black',
        className,
      )}
    >
      <div
        ref={stageRef}
        data-slide-deck-stage
        style={{
          width: SLIDE_DECK_CANVAS.width,
          height: SLIDE_DECK_CANVAS.height,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
        className="relative shrink-0"
      >
        {slides.map((slide, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={index}
              data-screen-label={`${String(index + 1).padStart(2, '0')} Slide`}
              data-slide-index={index}
              data-slide-active={isActive ? 'true' : 'false'}
              aria-hidden={!isActive}
              style={{
                width: SLIDE_DECK_CANVAS.width,
                height: SLIDE_DECK_CANVAS.height,
                visibility: isActive ? 'visible' : 'hidden',
                pointerEvents: isActive ? 'auto' : 'none',
              }}
              className="group/slide absolute inset-0"
            >
              {slide}
            </div>
          );
        })}
      </div>

      {total > 1 && !isFullscreen && (
        <SlideDeckChrome
          activeIndex={activeIndex}
          total={total}
          onPrev={() => prev('click')}
          onNext={() => next('click')}
          visible={overlayVisible}
        />
      )}

      <style>{`
        [data-slide-deck-stage] > [data-slide-index] > section {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }
        @media print {
          [data-slide-deck-root] {
            position: static !important;
            background: white !important;
            height: auto !important;
            width: auto !important;
            display: block !important;
          }
          [data-slide-deck-stage] {
            transform: none !important;
            width: ${SLIDE_DECK_CANVAS.width}px !important;
            height: ${SLIDE_DECK_CANVAS.height}px !important;
          }
          [data-slide-deck-stage] > [data-slide-index] {
            position: relative !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-after: always;
            break-after: page;
          }
        }
      `}</style>
    </div>
  );
}

interface ChromeProps {
  activeIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  visible: boolean;
}

const PILL_CLASS =
  'rounded-full bg-black/70 px-3 py-1 text-sm text-white ring-1 ring-white/15 backdrop-blur transition hover:bg-black/80 disabled:opacity-40';

function SlideDeckChrome({ activeIndex, total, onPrev, onNext, visible }: ChromeProps) {
  return (
    <div
      data-slide-deck-chrome
      onDoubleClick={(event) => event.stopPropagation()}
      className={cn(
        'fixed inset-x-0 bottom-8 flex items-center justify-center gap-3 transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={activeIndex === 0}
        aria-label="Previous slide"
        className={PILL_CLASS}
      >
        ←
      </button>
      <div className={cn(PILL_CLASS, 'px-4')}>
        {activeIndex + 1} / {total}
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={activeIndex === total - 1}
        aria-label="Next slide"
        className={PILL_CLASS}
      >
        →
      </button>
    </div>
  );
}

SlideDeckHarness.displayName = 'SlideDeckHarness';
